/**
 * Types, parser and i18n label helpers for the `employee_data` change-request
 * family (#1766): an employee's own personal-data change, rendered in the
 * triage inbox as a before-and-after diff. Re-exported from `./changeRequest`.
 */

import type { I18nLike } from './changeRequest';

// Sections (`sub_type`) of an `employee_data` request -- mirrors the backend
// EmployeeDataSection* constants.
export const EMPLOYEE_DATA_SECTIONS = ['address', 'phone', 'emergency_contacts'] as const;
export type EmployeeDataSection = (typeof EMPLOYEE_DATA_SECTIONS)[number];

// Field keys per section, in the fixed display order the backend emits.
export const EMPLOYEE_DATA_ADDRESS_KEYS = ['street', 'streetNumber', 'info2', 'zip', 'city', 'country'] as const;
export const EMPLOYEE_DATA_PHONE_KEYS = ['phone', 'phoneCountryCode'] as const;
export const EMPLOYEE_DATA_CONTACT_KEYS = ['name', 'relationship', 'phone', 'email', 'comment'] as const;

export const EMPLOYEE_DATA_CONTACT_OPS = ['add', 'edit', 'remove'] as const;
export type EmployeeDataContactOp = (typeof EMPLOYEE_DATA_CONTACT_OPS)[number];

/** One changed key: the value at submit (`old`) and the requested value (`new`). */
export interface EmployeeDataField {
  key: string;
  old: string | null;
  new: string | null;
}

/** One emergency-contact change; `id` is absent for an `add`. */
export interface EmployeeDataContactChange {
  op: EmployeeDataContactOp;
  id?: string;
  fields: EmployeeDataField[];
}

/**
 * Canonical `employee_data` executor body (#1766) -- the server-snapshotted
 * before-and-after diff. `address` / `phone` carry `fields` and an empty
 * `contacts`; `emergency_contacts` carries `contacts` and an empty `fields`.
 */
export interface EmployeeDataBody {
  employeeId: string;
  employeeName: string;
  section: EmployeeDataSection;
  fields: EmployeeDataField[];
  contacts: EmployeeDataContactChange[];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function isNullableString(v: unknown): v is string | null {
  return v === null || typeof v === 'string';
}

function isEmployeeDataFieldList(v: unknown, allowedKeys: readonly string[]): v is EmployeeDataField[] {
  return (
    Array.isArray(v) &&
    v.every(
      (f) =>
        isRecord(f) &&
        typeof f.key === 'string' &&
        allowedKeys.includes(f.key) &&
        isNullableString(f.old) &&
        isNullableString(f.new),
    )
  );
}

// Go marshals a nil slice as `null`; treat a missing or null list as empty.
function listOrEmpty(v: unknown): unknown {
  return v === undefined || v === null ? [] : v;
}

function isEmployeeDataContactList(v: unknown): v is Array<Record<string, unknown>> {
  return (
    Array.isArray(v) &&
    v.every(
      (c) =>
        isRecord(c) &&
        (EMPLOYEE_DATA_CONTACT_OPS as readonly unknown[]).includes(c.op) &&
        (c.id === undefined || typeof c.id === 'string') &&
        isEmployeeDataFieldList(listOrEmpty(c.fields), EMPLOYEE_DATA_CONTACT_KEYS),
    )
  );
}

/**
 * Type guard + normaliser for the pinned `employee_data` executor body.
 * Branches on `section`: a key outside the section's set, or a list the
 * section never carries, fails (returns null) so the caller can fall back to
 * the raw payload. Missing / null lists come back as empty arrays.
 */
export function parseEmployeeDataBody(body: unknown): EmployeeDataBody | null {
  if (!isRecord(body)) return null;
  if (typeof body.employeeId !== 'string' || typeof body.employeeName !== 'string') return null;
  const fields = listOrEmpty(body.fields);
  const contacts = listOrEmpty(body.contacts);
  const base = { employeeId: body.employeeId, employeeName: body.employeeName };
  switch (body.section) {
    case 'address':
    case 'phone': {
      const keys = body.section === 'address' ? EMPLOYEE_DATA_ADDRESS_KEYS : EMPLOYEE_DATA_PHONE_KEYS;
      if (!isEmployeeDataFieldList(fields, keys)) return null;
      if (!Array.isArray(contacts) || contacts.length > 0) return null;
      return { ...base, section: body.section, fields, contacts: [] };
    }
    case 'emergency_contacts': {
      if (!Array.isArray(fields) || fields.length > 0) return null;
      if (!isEmployeeDataContactList(contacts)) return null;
      return {
        ...base,
        section: body.section,
        fields: [],
        contacts: contacts.map((c) => ({
          op: c.op as EmployeeDataContactOp,
          id: typeof c.id === 'string' ? c.id : undefined,
          fields: listOrEmpty(c.fields) as EmployeeDataField[],
        })),
      };
    }
    default:
      return null;
  }
}

/** Localized label of an `employee_data` section. Returns the raw value for an unknown one. */
export function getEmployeeDataSectionLabel(i18n: I18nLike, section: string | undefined): string {
  switch (section) {
    case 'address':
      return i18n._({ id: 'changeRequest.employeeData.section.address', message: 'Address' });
    case 'phone':
      return i18n._({ id: 'changeRequest.employeeData.section.phone', message: 'Phone' });
    case 'emergency_contacts':
      return i18n._({ id: 'changeRequest.employeeData.section.emergencyContacts', message: 'Emergency contacts' });
    default:
      return section ?? '';
  }
}

/** Localized label of an `employee_data` field key. Returns the raw key for an unknown one. */
export function getEmployeeDataFieldLabel(i18n: I18nLike, key: string): string {
  switch (key) {
    case 'street':
      return i18n._({ id: 'changeRequest.employeeData.field.street', message: 'Street' });
    case 'streetNumber':
      return i18n._({ id: 'changeRequest.employeeData.field.streetNumber', message: 'Street number' });
    case 'info2':
      return i18n._({ id: 'changeRequest.employeeData.field.info2', message: 'Address line 2' });
    case 'zip':
      return i18n._({ id: 'changeRequest.employeeData.field.zip', message: 'Postal code' });
    case 'city':
      return i18n._({ id: 'changeRequest.employeeData.field.city', message: 'City' });
    case 'country':
      return i18n._({ id: 'changeRequest.employeeData.field.country', message: 'Country' });
    case 'phone':
      return i18n._({ id: 'changeRequest.employeeData.field.phone', message: 'Phone' });
    case 'phoneCountryCode':
      return i18n._({ id: 'changeRequest.employeeData.field.phoneCountryCode', message: 'Phone country code' });
    case 'name':
      return i18n._({ id: 'changeRequest.employeeData.field.name', message: 'Name' });
    case 'relationship':
      return i18n._({ id: 'changeRequest.employeeData.field.relationship', message: 'Relationship' });
    case 'email':
      return i18n._({ id: 'changeRequest.employeeData.field.email', message: 'Email' });
    case 'comment':
      return i18n._({ id: 'changeRequest.employeeData.field.comment', message: 'Comment' });
    default:
      return key;
  }
}

/** Localized badge text of an emergency-contact op. */
export function getEmployeeDataContactOpLabel(i18n: I18nLike, op: EmployeeDataContactOp): string {
  switch (op) {
    case 'add':
      return i18n._({ id: 'changeRequest.employeeData.op.add', message: 'Added' });
    case 'edit':
      return i18n._({ id: 'changeRequest.employeeData.op.edit', message: 'Changed' });
    case 'remove':
      return i18n._({ id: 'changeRequest.employeeData.op.remove', message: 'Removed' });
  }
}
